import { Component, Host, h, EventEmitter, Event, Element, Prop } from '@stencil/core';
import { keyboardClickProps } from '../../utils/keyboard';
import { showSlotContent } from '../../utils/slotHelpers';

export interface AhaTab extends HTMLAhaTabElement {}

/**
 *
 * @slot badge - A number or some short text that appears next to the tab, usually to show a count of records inside or to indicate a number of unread items.
 *
 */
@Component({
  tag: 'aha-tab',
  styleUrl: 'aha-tab.css',
  shadow: true,
})
export class AhaTab {
  @Element() host!: HTMLAhaTabElement;

  /** @ignore */
  @Event() tabChosen!: EventEmitter<HTMLAhaTabElement>;

  /**
   * Whether this is the currently selected tab.
   */
  @Prop({ reflect: true }) selected: boolean = false;

  /**
   * @deprecated
   * Use badge slot.
   */
  @Prop() badge?: string;

  /**
   * Supply an `href` to have this tab operate as a link rather than a tab. If
   * you've done this to one tab, you must do it to all tabs.
   */
  @Prop() href?: string;
  /** Equivalent to `<a target>`. Has no effect unless `href` is set. */
  @Prop() target?: HTMLAnchorElement['target'];
  /** Equivalent to `<a rel>`. Has no effect unless `href` is set. */
  @Prop() rel?: HTMLAnchorElement['rel'];
  private get safeRel() {
    if (this.target?.includes('_blank')) {
      return `${this.rel || ''} noopener noreferrer`;
    } else {
      return this.rel;
    }
  }

  /** @ignore */
  @Prop() inMenu = false;

  private handleClick = (event: MouseEvent) => {
    if (this.href) {
      return;
    }

    this.host.focus();
    this.tabChosen.emit(this.host);
  };

  render() {
    const interior = (
      <div id="content">
        <div class="slot">
          <slot></slot>
        </div>
        <slot name="badge" onSlotchange={showSlotContent}>
          {this.badge && <span class="badge">{this.badge}</span>}
        </slot>
      </div>
    );

    return (
      <Host
        role={this.href ? 'none' : 'tab'}
        onClick={this.handleClick}
        tabIndex={this.selected ? 0 : -1}
        aria-selected={this.selected ? 'true' : 'false'}
        data-in-menu={this.inMenu}
        {...keyboardClickProps()}
      >
        {this.href ? (
          <a href={this.href} rel={this.safeRel} target={this.target}>
            {interior}
          </a>
        ) : (
          interior
        )}
      </Host>
    );
  }
}
